import { Button, useColorModeValue } from '@chakra-ui/react';
import moment from 'moment';
import { RiImageFill, RiVideoFill } from 'react-icons/ri';

const getFileType = (name) => {
    const extension = name?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
        case 'jpeg':
        case 'jpg':
        case 'png':
        case 'gif':
        case 'bmp':
            return { icon: <RiImageFill />, label: 'Image', type: 'image' };
        case 'mp4':
        case 'avi':
        case 'mov':
        case 'mkv':
        case 'webm':
            return { icon: <RiVideoFill />, label: 'Video', type: 'video' };
        default:
            return { icon: null, label: 'Unknown', type: 'unknown' };
    }
};

const toCurrency = (amount, currency) => {
    const symbol = currency === 'INR' ? '₹' : ''
    let number = parseFloat(amount);
    return symbol + number.toLocaleString(currency);
}

function getExtension(filename) {
    return filename?.split('.').pop()
}

function isJsonString(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

function hasDecimal(number) {
    const numberAsString = number.toString();
    return numberAsString.includes('.');
}

function truncateString(str, maxLength) {
    if (!str || typeof str !== 'string') {
        return ''; 
    }

    if (str.length <= maxLength) {
        return str;
    } else {
        return str.slice(0, maxLength) + '...';
    }
}

const truncateName = (name, maxLength = 14) => {
    if (name?.length > maxLength) {
        return name?.substring(0, maxLength) + '...';
    }
    return name;
};

const isValidCustomScript = (text) => {
    if (!text.trim()) return false;
  
    const forbiddenPatterns = [/##/, /\[\]/, /--/, /\*\*/, /Genre\s*:/i];
    const hasForbidden = forbiddenPatterns.some((regex) => regex.test(text));
    const hasInappropriate = /explicit|violent/i.test(text); 
  
    return !hasForbidden && !hasInappropriate && text.trim().length >= 40; 
};

const flattenTargeting = (data) => {
    if (!data || Object.keys(data)?.length === 0) {
        return;
    }
    const targetFields = [
        'interests',
        'education_schools',
        'education_majors',
        'work_positions',
        'work_employers',
        'relationship_statuses',
        'interested_in',
        'user_adclusters',
        'college_years',
        'education_statuses',
        'family_statuses',
        'industries',
        'life_events',
        'politics',
        'behaviors',
        'income',
        'net_worth',
        'home_type',
        'home_ownership',
        'home_value',
        'ethnic_affinity',
        'generation',
        'household_composition',
        'moms',
        'office_type',
        'location_categories'
    ];

    // Extract and flatten only the fields that are present in the object
    const resultArray = targetFields
        .filter(field => data[field])  // Only include fields that exist
        .flatMap(field => data[field]); // Flatten arrays into one

    return resultArray;
};

const getEndOfNextMonth = () => {
    return moment().add(1, 'month').endOf('month').toDate();
};

const removeEmptyValues = (obj) => {
    if (Array.isArray(obj)) {
        return obj
            .map(removeEmptyValues)
            .filter((val) => val != null && val !== '' && !(Array.isArray(val) && val.length === 0));
    } else if (typeof obj === 'object' && obj !== null) {
        return Object.fromEntries(
            Object.entries(obj)
                .map(([k, v]) => [k, removeEmptyValues(v)])
                .filter(([k, v]) =>
                    v != null &&
                    v !== '' &&
                    !(Array.isArray(v) && v.length === 0) &&
                    !(
                        typeof v === 'object' &&
                        Object.keys(v).length === 0 &&
                        k !== 'customization_spec' // <-- keep if key is 'customization_spec'
                    )
                )
        );
    }
    return obj;
};


const formatErrorMessages = (errors) => {
    if (Array.isArray(errors)) {
        const uniqueErrors = new Set();
        errors.forEach(error => {
            Object.values(error).forEach(message => uniqueErrors.add(message));
        });
        return Array.from(uniqueErrors).join(', ');

    }
    return typeof errors === 'string' ? errors : 'An unknown error occurred';
};

const convertUnixToDate = (timestamp) => {
    return new Date(timestamp * 1000); // Convert Unix seconds to milliseconds
};

const formatUnixTimestamp = (timestamp) => {
    const date = convertUnixToDate(timestamp);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Adding leading zero
    const day = ('0' + date.getDate()).slice(-2); // Adding leading zero
    return `${year}-${month}-${day}`; // YYYY-MM-DD format
};

// Group the selected locations by country
const groupGeoLocations = (selectedLocations) => {
    selectedLocations.reduce((acc, location) => {
        const countryName = location.country_name;

        if (!acc[countryName]) {
            acc[countryName] = [];
        }

        acc[countryName].push(location);

        return acc;
    }, {});
}

const getFileNameWithoutExtension = (filename) => {
    if (!filename || typeof filename !== 'string') return '';

    if (filename.split('.')?.length === 1) return filename;

    return filename.split('.').slice(0, -1).join('.');
};

const formatDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
  
    return `${year}-${month}-${day} ${hours}:${minutes}`;
};

function base64ToFile(base64Data, filename, mimeType) {
    const byteString = atob(base64Data.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
  
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
  
    return new File([ab], filename, { type: mimeType });
}

async function urlToFile(url, filename, mimeType) {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType });
}

const overlayImages = (backgroundUrl, baseImageBase64) => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const background = new window.Image();
        const base = new window.Image();


        background.crossOrigin = "anonymous";
        base.crossOrigin = "anonymous";

        let loaded = 0;

        const checkAndCompose = () => {
            loaded++;
            if (loaded === 2) {
                canvas.width = background.width;
                canvas.height = background.height;

                ctx.drawImage(background, 0, 0, canvas.width, canvas.height);
                ctx.drawImage(base, 0, 0, canvas.width, canvas.height);

                resolve(canvas.toDataURL('image/png'));
            }
        };

        background.onload = checkAndCompose;
        base.onload = checkAndCompose;

        background.onerror = reject;
        base.onerror = reject;

        background.src = backgroundUrl;
        base.src = baseImageBase64;
    });
};

const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

function formatCardIndices(indices) {
    if (indices.length === 1) return indices[0];
    if (indices.length === 2) return `${indices[0]} and ${indices[1]}`;
    const last = indices[indices.length - 1];
    return `${indices.slice(0, -1).join(', ')} and ${last}`;
}

const formatNumber = (num) => (num % 1 === 0 ? num.toFixed(0) : num.toFixed(2));

const styleForOverlayPosition = (pos) => {
    switch (pos) {
      case 'top_left':      return { top: '0%',   left: '0%',   transform: 'translate(0, 0)' };
      case 'top':           return { top: '0%',   left: '50%',  transform: 'translate(-50%, 0)' };
      case 'top_right':     return { top: '0%',   left: '100%', transform: 'translate(-100%, 0)' };
      case 'left':          return { top: '50%',  left: '0%',   transform: 'translate(0, -50%)' };
      case 'center':        return { top: '50%',  left: '50%',  transform: 'translate(-50%, -50%)' };
      case 'right':         return { top: '50%',  left: '100%', transform: 'translate(-100%, -50%)' };
      case 'bottom_left':   return { top: '100%', left: '0%',   transform: 'translate(0, -100%)' };
      case 'bottom':        return { top: '100%', left: '50%',  transform: 'translate(-50%, -100%)' };
      case 'bottom_right':  return { top: '100%', left: '100%', transform: 'translate(-100%, -100%)' };
      default:              return { top: '50%',  left: '50%',  transform: 'translate(-50%, -50%)' };
    }
  };

const getStepMeta = (step, mediaType) => {
    const isVideo = mediaType === "video";
  
    if (step === "Media") {
      return {
        title: "Media",
        desc: "Select one or more media files to create multiple ads across the selected Ad Accounts at once",
      };
    }
  
    // Replace step
    return {
      title: "Select media crop for placements",
      desc: isVideo
        ? "Media that fill the crops of different placements may perform better. You can edit these crops or upload new media for each one. These crops are related to placements, not specific devices."
        : "We evaluated your image/video and replace it to fill the placement when we think it may perform better. You can edit these by replacing or upload new media for each placement by hovering over the image/video.",
    };
  };

  function StepButton({ children, isActive, onClick, isDisabled = false }) {
    const activeBg = useColorModeValue("brand.800", "#524789");
    const activeColor = useColorModeValue("white", "gray.900");
    const hoverBg = useColorModeValue("gray.100", "whiteAlpha.200");
  
    return (
      <Button
        w={'100%'}
        justifyContent="flex-start"
        variant={isActive ? "solid" : "ghost"}
        size="sm"
        onClick={onClick}
        bg={isActive ? activeBg : undefined}
        color={isActive ? activeColor : undefined}
        _hover={{ bg: isActive ? activeBg : hoverBg }}
        borderRadius="md"
        isDisabled={isDisabled}
      >
        {children}
      </Button>
    );
  }

    const getCallToActionOptions = (baseOptions, currentValue) => {
        if (!currentValue) return baseOptions;
        
        // Check if current value exists in options
        const valueExists = baseOptions.some(option => option.value === currentValue);
        
        // If it doesn't exist, append it to the options
        if (!valueExists) {
            return [
                ...baseOptions,
                {
                    value: currentValue,
                    label: currentValue.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
                }
            ];
        }
        
        return baseOptions;
    };

  export {
    base64ToFile,
    flattenTargeting,
    formatDateTime,
    formatDuration,
    formatErrorMessages,
    formatUnixTimestamp,
    getEndOfNextMonth,
    getExtension,
    getFileNameWithoutExtension,
    getFileType,
    groupGeoLocations,
    hasDecimal,
    isJsonString,
    isValidCustomScript,
    overlayImages,
    removeEmptyValues,
    toCurrency,
    truncateName,
    truncateString,
    urlToFile,
    formatCardIndices,
    formatNumber,
    styleForOverlayPosition,
    getStepMeta,
    StepButton,
    getCallToActionOptions
  };

